import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { iniciarSesionConGoogle, logout } from '../services/authService';
import { suscribirSesionExpirada } from '../../../shared/services/sessionExpiry';
import { AuthErrorCode } from '../domain/authErrors';
import { AuthStatus, LoginPage } from './LoginPage';

// SPEC-175 — la app entera queda detrás de login (RN-001: todo /api/* requiere sesión).
// SPEC-185 — el gate deja de renderizar un botón MUI genérico y pasa a renderizar la
// pantalla del handoff, además de poseer el estado del flujo (`authStatus`/`errorCode`).
//
// Importante: Firebase marca `user` en cuanto el popup resuelve, ANTES de que el backend
// verifique la autorización. Por eso el gate no puede decidir solo con `user`: mientras hay
// un intento en curso (loading/error/success) sigue mostrando el login, y solo entrega la
// app cuando el intento terminó bien o cuando la sesión venía restaurada del SDK (CA-009).

/** Handoff: el banner de éxito se ve ~400ms antes de navegar (RNF-001: < 500ms). */
const MS_BANNER_EXITO = 400;

export function LoginGate({ children }: PropsWithChildren<{}>) {
  const { user, loading } = useAuth();
  const [estado, setEstado] = useState<AuthStatus>('idle');
  const [codigoError, setCodigoError] = useState<AuthErrorCode | null>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // CA-008: cualquier request que reciba SESSION_EXPIRED devuelve al gate con el banner.
    return suscribirSesionExpirada(() => {
      setCodigoError('SESSION_EXPIRED');
      setEstado('error');
      void logout();
    });
  }, []);

  useEffect(() => {
    return () => {
      if (temporizador.current !== null) {
        clearTimeout(temporizador.current);
      }
    };
  }, []);

  const manejarInicioSesion = useCallback(async () => {
    setCodigoError(null);
    setEstado('loading');

    const resultado = await iniciarSesionConGoogle();

    if (resultado.estado === 'cancelado') {
      // CA-006 / RN-004: cerrar el popup no es un error — se vuelve a reposo, sin banner.
      setEstado('idle');
      return;
    }

    if (resultado.estado === 'error') {
      setCodigoError(resultado.codigo);
      setEstado('error');
      return;
    }

    setEstado('success');
    temporizador.current = setTimeout(() => setEstado('idle'), MS_BANNER_EXITO);
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const intentoEnCurso = estado !== 'idle';

  if (!user || intentoEnCurso) {
    return (
      <Box data-testid="login-gate">
        <LoginPage estado={estado} codigoError={codigoError} onIniciarSesion={manejarInicioSesion} />
      </Box>
    );
  }

  return <>{children}</>;
}
